import { useEffect, useState } from "react";
import { api } from "../lib/api";

/**
 * Reusable sponsored/promotional ad slot. Fetches an eligible ad from the backend
 * on mount, records the impression via the same call, and reports clicks before
 * navigating. Preserves aspect ratio and never crops the creative.
 */
export default function AdSlot({ placement, audience, className = "", onLoaded }) {
  const [ad, setAd] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const params = { placement };
    if (audience) params.audience = audience;
    api.get("/public/ads/next", { params })
      .then(r => { if (!cancelled) { setAd(r.data?.ad || null); onLoaded?.(r.data?.ad?.ad_id || null); } })
      .catch(() => setFailed(true));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placement, audience]);

  if (failed || !ad) return null;

  const onClick = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/public/ads/${ad.ad_id}/click`, null, { params: { placement } });
    } catch {}
    window.open(ad.destination_url, "_blank", "noopener,noreferrer");
  };

  // MPS house-ad creatives share one size class (~1575×1000, 1.575:1). Render at that class, never stretched or upscaled.
  return (
    <div data-testid={`ad-slot-${placement}`} data-ad-id={ad.ad_id}
          className={`relative mx-auto w-full max-w-[560px] rounded-xl overflow-hidden border border-slate-200 bg-white shadow-sm ${className}`}>
      <div className="absolute top-2 left-2 z-10 text-[9px] font-semibold uppercase tracking-widest bg-slate-900/80 text-white px-1.5 py-0.5 rounded">
        {ad.label || "Sponsored"}
      </div>
      <a href={ad.destination_url} target="_blank" rel="noopener noreferrer sponsored"
          onClick={onClick} data-testid={`ad-link-${ad.ad_id}`}
          aria-label={`${ad.alt_text} — opens in a new tab`}
          className="flex items-center justify-center w-full" style={{aspectRatio: "1575 / 1000"}}>
        <img src={ad.creative_url} alt={ad.alt_text}
              loading="lazy" decoding="async"
              className="max-w-full max-h-full w-auto h-auto object-contain block"/>
      </a>
    </div>
  );
}
